import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { GraduationCap, MapPin, Mail, ArrowLeft, Sun } from 'lucide-react';
import { InstructorAvatar } from '@/components/InstructorAvatar';
import SEO from '@/components/SEO';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';
import { SummerFoundingPartnerBadge } from '@/components/SummerFoundingPartnerBadge';
import { LocationAlertSignup } from '@/components/LocationAlertSignup';
import { resolveState } from '@/lib/usStates';

/**
 * State-scoped instructor hub — `/instructors/california`,
 * `/instructors/new-york`, etc. Built for SEO long-tail rankings on
 * "American Mahjong instructor in <State>" queries. Every entry is a
 * crawlable `<Link>` to the instructor's `/player/:id` profile.
 */
export default function InstructorsByStatePage() {
  const { state: stateParam } = useParams();
  const stateInfo = resolveState(stateParam || '');
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!stateInfo) { setLoading(false); return; }
    setLoading(true);
    api.get(`/instructors/by-state/${stateInfo.slug}`)
      .then(r => setInstructors(r.data.instructors || []))
      .catch(() => setInstructors([]))
      .finally(() => setLoading(false));
  }, [stateInfo]);

  // 404-style redirect for unknown slugs. Returning Navigate keeps the
  // route hierarchy clean — `/instructors/blarg` lands on /instructors.
  if (!stateInfo) return <Navigate to="/instructors" replace />;

  // Canonicalise: if the URL used a code (e.g. /instructors/ca) or a
  // case-different slug, replace with the lowercase slug form.
  if (stateParam !== stateInfo.slug) {
    return <Navigate to={`/instructors/${stateInfo.slug}`} replace />;
  }

  const title = `American Mahjong Instructors in ${stateInfo.name}`;
  const description = `Find American Mahjong instructors in ${stateInfo.name} offering group classes, private lessons, and Mahjong events. ${instructors.length} ${instructors.length === 1 ? 'instructor' : 'instructors'} currently listed on Meet For Mahjong.`;

  // SEO: ItemList of Person/instructors in this state. Helps Google
  // understand the page is a "directory of instructors in <state>".
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    numberOfItems: instructors.length,
    itemListElement: instructors.map((inst, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://meetformahjong.com/player/${inst.id}`,
      name: inst.display_name,
    })),
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="instructors-by-state-page">
      <SEO
        title={title}
        description={description}
        path={`/instructors/${stateInfo.slug}`}
        jsonLd={itemListJsonLd}
      />

      <div className="mb-6">
        <Link
          to="/instructors"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#b456df]"
          data-testid="state-hub-back-to-all-instructors"
        >
          <ArrowLeft className="w-4 h-4" /> All instructors
        </Link>
      </div>

      <div className="text-center mb-10">
        <div className="inline-block px-3 py-1 rounded-full bg-[#b456df]/10 text-[#b456df] text-xs font-semibold tracking-wider uppercase mb-3">
          {stateInfo.name}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {title}
        </h1>
        <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
          {instructors.length > 0
            ? `Connect directly with ${instructors.length} American Mahjong ${instructors.length === 1 ? 'instructor' : 'instructors'} in ${stateInfo.name}. Most offer beginner classes, intermediate lessons, and private coaching, plus host their own local Mahjong events.`
            : `No American Mahjong instructors are listed in ${stateInfo.name} yet — be the first! Meet For Mahjong is growing and new instructors join every week. If you teach in ${stateInfo.name}, you can sign up free and reach players in your area.`}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-[#b456df] border-t-transparent rounded-full" />
        </div>
      ) : instructors.length === 0 ? (
        <div className="text-center py-12" data-testid="state-hub-empty-state">
          <GraduationCap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
            Looking for Mahjong lessons in {stateInfo.name}? You can post a "Find an instructor" request in the community, browse upcoming events, or sign up to be notified when a new instructor joins your area.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to={`/events/${stateInfo.slug}`}>
              <Button className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]">
                See Mahjong events in {stateInfo.name}
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="rounded-full">Sign up free</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="state-hub-instructors-grid">
          {instructors.map(inst => (
            <Card
              key={inst.id}
              className={`border-slate-100 hover:shadow-md transition-all ${inst.is_premium_instructor ? 'ring-1 ring-amber-300/60' : ''}`}
              data-testid={`state-hub-instructor-${inst.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <InstructorAvatar
                    photoUrl={inst.profile_photo}
                    name={inst.display_name}
                    testId={`state-hub-instructor-avatar-${inst.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/player/${inst.id}`}
                        className="font-semibold text-lg hover:text-[#b456df]"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {inst.display_name}
                      </Link>
                      {inst.is_premium_instructor && <PremiumInstructorBadge size="sm" />}
                      {inst.is_summer_founding_partner && <SummerFoundingPartnerBadge size="sm" />}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap">
                      {(inst.city || inst.zip_code) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {inst.city ? `${inst.city}, ${stateInfo.code}` : inst.zip_code}
                        </span>
                      )}
                      <span className="capitalize">{inst.skill_level} level</span>
                    </div>
                    {inst.instructor_bio && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{inst.instructor_bio}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(inst.instructor_lesson_types || []).map(t => (
                        <span key={t} className="px-2 py-0.5 rounded-full bg-[#b456df]/10 text-[#b456df] text-xs capitalize">{t}</span>
                      ))}
                    </div>
                    {inst.instructor_rate && <p className="text-xs text-slate-400 mt-2">Rate: {inst.instructor_rate}</p>}
                    <div className="flex gap-2 mt-3">
                      <Link to={`/player/${inst.id}`}>
                        <Button size="sm" className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] text-xs">
                          View profile
                        </Button>
                      </Link>
                      {inst.email && (
                        <a href={`mailto:${inst.email}`}>
                          <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs">
                            <Mail className="w-3 h-3" /> Email
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cross-link to events hub in the same state — Google loves
          related-content internal linking. */}
      <div className="mt-10 text-center">
        <Link
          to={`/events/${stateInfo.slug}`}
          className="inline-flex items-center gap-2 text-sm text-[#56c6df] hover:underline"
          data-testid="state-hub-cross-link-events"
        >
          <Sun className="w-4 h-4" />
          See upcoming American Mahjong events in {stateInfo.name} →
        </Link>
      </div>

      <LocationAlertSignup
        type="instructor"
        stateSlug={stateInfo.slug}
        locationLabel={stateInfo.name}
      />
    </div>
  );
}
